import type {
  IAction,
  IActionDefinitionDto,
  IDisplayItem,
  IProject,
  IStartProcessTrigger,
  IStartProcessTriggerSourceType
} from "@kuika/kuika-cl-model";

const START_PROCESS_ACTION_NAME = "StartProcess";
const WORKFLOW_PARAM_NAME = "workflow";

export const isStartProcessAction = (
  action: IAction,
  actionDefinitions: IActionDefinitionDto[] | undefined
): boolean => {
  if (!action || !action.actionDefinitionId) return false;
  const def = actionDefinitions?.find((d) => d.id === action.actionDefinitionId);
  return def?.name === START_PROCESS_ACTION_NAME;
};

export const extractTargetWorkflowIdFromStartProcessAction = (action: IAction): string | undefined => {
  const workflowParam = action.parameters?.find((p) => p.name === WORKFLOW_PARAM_NAME);
  const value = workflowParam?.symbol?.fixedValue;
  if (typeof value !== "string" || value.length === 0) return undefined;
  return value;
};

export interface ICollectedStartProcessAction {
  actionId: string;
  workflowId: string;
}

const collectChildActionLists = (action: IAction): IAction[][] => {
  const lists: IAction[][] = [];
  if (action.ifThenElseData) {
    if (Array.isArray(action.ifThenElseData.ifActions)) lists.push(action.ifThenElseData.ifActions);
    if (Array.isArray(action.ifThenElseData.elseActions)) lists.push(action.ifThenElseData.elseActions);
  }
  if (action.forEachData && Array.isArray(action.forEachData.actions)) {
    lists.push(action.forEachData.actions);
  }
  if (action.recurringActionsData && Array.isArray(action.recurringActionsData.recurringActions)) {
    lists.push(action.recurringActionsData.recurringActions);
  }
  if (action.mqttSubscribeActionData && Array.isArray(action.mqttSubscribeActionData.mqttSubscribeActions)) {
    lists.push(action.mqttSubscribeActionData.mqttSubscribeActions);
  }
  return lists;
};

export const collectStartProcessActionsInActionTree = (
  actions: IAction[] | undefined,
  actionDefinitions: IActionDefinitionDto[] | undefined,
  out: ICollectedStartProcessAction[] = []
): ICollectedStartProcessAction[] => {
  if (!actions) return out;
  for (const action of actions) {
    if (!action) continue;
    if (isStartProcessAction(action, actionDefinitions)) {
      const workflowId = extractTargetWorkflowIdFromStartProcessAction(action);
      if (workflowId && action.id) {
        out.push({ actionId: action.id, workflowId });
      }
    }
    for (const childList of collectChildActionLists(action)) {
      collectStartProcessActionsInActionTree(childList, actionDefinitions, out);
    }
  }
  return out;
};

/**
 * Walks a DisplayItem subtree (the given DI and all descendants) and collects
 * every StartProcess action found in any component's events. Used when a
 * component or container is removed from the canvas to identify which
 * StartProcess triggers must be pruned from the project's reverse index.
 */
export const collectStartProcessActionsInDisplayItemSubtree = (
  rootDi: IDisplayItem | undefined,
  actionDefinitions: IActionDefinitionDto[] | undefined
): ICollectedStartProcessAction[] => {
  const out: ICollectedStartProcessAction[] = [];
  if (!rootDi) return out;
  const queue: IDisplayItem[] = [rootDi];
  while (queue.length > 0) {
    const current = queue.shift()!;
    const events = current.item?.events ?? [];
    for (const evt of events) {
      collectStartProcessActionsInActionTree(evt?.actions, actionDefinitions, out);
    }
    if (current.children?.length) {
      queue.push(...current.children);
    }
  }
  return out;
};

const cloneIndex = (project: IProject): Record<string, IStartProcessTrigger[]> => {
  const result: Record<string, IStartProcessTrigger[]> = {};
  const existing = project.startProcessTriggers;
  if (!existing) return result;
  for (const key of Object.keys(existing)) {
    result[key] = existing[key] ? [...existing[key]] : [];
  }
  return result;
};

const isSameTrigger = (a: IStartProcessTrigger, b: IStartProcessTrigger): boolean =>
  a.sourceType === b.sourceType && a.sourceId === b.sourceId && a.actionId === b.actionId;

export const addStartProcessTrigger = (
  project: IProject,
  workflowId: string,
  trigger: IStartProcessTrigger
): IProject => {
  if (!workflowId || !trigger.sourceId || !trigger.actionId) return project;
  const index = cloneIndex(project);
  const list = index[workflowId] ? [...index[workflowId]] : [];
  const existingIdx = list.findIndex((t) => isSameTrigger(t, trigger));
  if (existingIdx >= 0) {
    list[existingIdx] = trigger;
  } else {
    list.push(trigger);
  }
  index[workflowId] = list;
  return { ...project, startProcessTriggers: index };
};

export const removeStartProcessTrigger = (
  project: IProject,
  workflowId: string,
  sourceType: IStartProcessTriggerSourceType,
  sourceId: string,
  actionId: string
): IProject => {
  if (!project.startProcessTriggers || !project.startProcessTriggers[workflowId]) return project;
  const index = cloneIndex(project);
  const list = index[workflowId].filter(
    (t) => !(t.sourceType === sourceType && t.sourceId === sourceId && t.actionId === actionId)
  );
  if (list.length === 0) {
    delete index[workflowId];
  } else {
    index[workflowId] = list;
  }
  return { ...project, startProcessTriggers: index };
};

export const removeAllStartProcessTriggersBySource = (
  project: IProject,
  sourceType: IStartProcessTriggerSourceType,
  sourceId: string
): IProject => {
  if (!project.startProcessTriggers) return project;
  const index = cloneIndex(project);
  let changed = false;
  for (const workflowId of Object.keys(index)) {
    const filtered = index[workflowId].filter((t) => !(t.sourceType === sourceType && t.sourceId === sourceId));
    if (filtered.length !== index[workflowId].length) {
      changed = true;
      if (filtered.length === 0) {
        delete index[workflowId];
      } else {
        index[workflowId] = filtered;
      }
    }
  }
  return changed ? { ...project, startProcessTriggers: index } : project;
};

export const removeAllStartProcessTriggersByWorkflow = (project: IProject, workflowId: string): IProject => {
  if (!project.startProcessTriggers || !project.startProcessTriggers[workflowId]) return project;
  const index = cloneIndex(project);
  delete index[workflowId];
  return { ...project, startProcessTriggers: index };
};

/**
 * Drop trigger entries whose source entity no longer exists. Defensive cleanup
 * for orphan rows that can accumulate from legacy data or missed cascade hooks.
 * Returns the same project reference when nothing was pruned so callers can use
 * a cheap ref check to decide whether to fire updateProject.
 */
export const pruneOrphanStartProcessTriggers = (
  project: IProject,
  existingScreenIds: Set<string>,
  existingScheduledJobIds: Set<string>,
  existingApiManagementIds: Set<string>
): IProject => {
  if (!project.startProcessTriggers) return project;
  const index: Record<string, IStartProcessTrigger[]> = {};
  let changed = false;
  for (const workflowId of Object.keys(project.startProcessTriggers)) {
    const entries = project.startProcessTriggers[workflowId] ?? [];
    const valid: IStartProcessTrigger[] = [];
    for (const t of entries) {
      const stillExists =
        (t.sourceType === "Screen" && existingScreenIds.has(t.sourceId)) ||
        (t.sourceType === "ScheduledJob" && existingScheduledJobIds.has(t.sourceId)) ||
        (t.sourceType === "ApiManagement" && existingApiManagementIds.has(t.sourceId));
      if (stillExists) {
        valid.push(t);
      } else {
        changed = true;
      }
    }
    if (valid.length > 0) {
      index[workflowId] = valid;
    } else if (entries.length > 0) {
      // workflow key dropped entirely
      changed = true;
    }
  }
  return changed ? { ...project, startProcessTriggers: index } : project;
};

interface IApplyStartProcessTriggerDiffParams {
  project: IProject;
  sourceType: IStartProcessTriggerSourceType;
  sourceId: string;
  previousSnapshot: ICollectedStartProcessAction[];
  currentSnapshot: ICollectedStartProcessAction[];
}

/**
 * Given two snapshots of StartProcess actions in an action tree (taken before
 * and after a mutation), apply the necessary add/remove operations to keep
 * project.startProcessTriggers consistent.
 *
 * Callers typically:
 *   const previousSnapshot = collectStartProcessActionsInActionTree(actions, defs);
 *   // ...mutate the action list...
 *   const currentSnapshot = collectStartProcessActionsInActionTree(actions, defs);
 *   const nextProject = applyStartProcessTriggerDiff({...});
 */
export const applyStartProcessTriggerDiff = ({
  project,
  sourceType,
  sourceId,
  previousSnapshot,
  currentSnapshot
}: IApplyStartProcessTriggerDiffParams): IProject => {
  if (!sourceId) return project;

  const prevByActionId = new Map<string, string>();
  for (const item of previousSnapshot) prevByActionId.set(item.actionId, item.workflowId);
  const currByActionId = new Map<string, string>();
  for (const item of currentSnapshot) currByActionId.set(item.actionId, item.workflowId);

  let next = project;

  for (const [actionId, prevWfId] of prevByActionId) {
    const currWfId = currByActionId.get(actionId);
    if (currWfId === undefined) {
      next = removeStartProcessTrigger(next, prevWfId, sourceType, sourceId, actionId);
    } else if (currWfId !== prevWfId) {
      next = removeStartProcessTrigger(next, prevWfId, sourceType, sourceId, actionId);
      next = addStartProcessTrigger(next, currWfId, { sourceType, sourceId, actionId });
    }
  }

  for (const [actionId, currWfId] of currByActionId) {
    if (!prevByActionId.has(actionId)) {
      next = addStartProcessTrigger(next, currWfId, { sourceType, sourceId, actionId });
    }
  }

  return next;
};
